<template>
  <div
    class="w-full h-auto min-h-screen mt-3 justify-between space-y-9 rounded-t-[20px] bg-gray-50 dark:bg-background"
  >
    <div class="mx-4 md:mx-8 my-6">
      <div
        v-if="showBanner"
        class="flex items-center justify-between bg-card border border-border rounded-lg px-4 py-3 mb-6 shadow-sm"
      >
        <div class="flex items-center space-x-3">
          <div
            class="w-8 h-8 flex items-center justify-center rounded-lg shadow-sm border border-border"
          >
            <svg
              class="w-5 h-5 text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
              />
            </svg>
          </div>
          <span class="text-sm text-muted-foreground">
            <span class="font-semibold">New!</span> We just released a new
            version of Progress Tracker Desktop.
            <a
              href="https://www.edgespace.africa/products/pt-desktop/"
              target="_blank"
              rel="noopener noreferrer"
              class="ml-1 text-blue-600 hover:text-blue-700 underline"
              >Read more</a
            >
          </span>
        </div>
        <div class="flex items-center space-x-2">
          <button
            @click="downloadApp"
            class="px-4 py-2 text-sm font-medium text-foreground bg-card border border-border rounded-lg hover:bg-muted transition-colors"
          >
            Download Now
          </button>
          <button
            @click="showBanner = false"
            class="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>

      <div class="md:flex items-center justify-between mb-6">
        <div class="space-y-1">
          <p class="text-foreground text-[40px]">Your learning dashboard</p>
          <p class="text-foreground text-[20px]">
            Build a skill path. Track progress. Improve consistently.
          </p>
        </div>

        <div class="md:flex gap-2">
          <button
            @click="navigateToCreate"
            class="flex bg-primary text-primary-foreground dark:bg-secondary dark:text-foreground dark:hover:bg-border text-[13px] font-medium px-6 py-3 gap-1 items-center rounded-full h-[42px] whitespace-nowrap"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8 3.33301V12.6663"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
              />
              <path
                d="M12.6667 8H3.33337"
                stroke="white"
                stroke-width="1.5"
                stroke-linecap="round"
              />
            </svg>
            Create a new Skill Path
          </button>
        </div>
      </div>

      <!-- Main Content Grid -->
      <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <!-- Left Column - Skill Paths Lists -->
        <div class="lg:col-span-2 space-y-6">
          <!-- Current Skill Paths -->
          <div
            class="border border-border rounded-[12px] px-3 md:px-6 py-2 md:py-6 bg-card"
          >
            <div class="flex items-center justify-between mb-4">
              <h2 class="text-[15px] font-semibold text-foreground">
                Current Skill Paths
              </h2>
              <button
                type="button"
                v-if="learningGoals.length > 0"
                @click="onSeeMoreClick"
                class="inline-flex items-center gap-2 bg-black text-white dark:bg-muted dark:text-foreground text-[11px] font-semibold px-3 py-1.5 rounded-full hover:opacity-90 transition-colors"
              >
                See more
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.99935 1.33301C11.6727 1.33301 14.666 4.32634 14.666 7.99967C14.666 11.673 11.6727 14.6663 7.99935 14.6663C4.32602 14.6663 1.33268 11.673 1.33268 7.99967C1.33268 4.32634 4.32602 1.33301 7.99935 1.33301ZM6.80602 9.99967C6.61268 10.193 6.61268 10.513 6.80602 10.7063C6.90602 10.8063 7.03268 10.853 7.15935 10.853C7.28602 10.853 7.41268 10.8063 7.51268 10.7063L9.86602 8.35301C10.0593 8.15967 10.0593 7.83967 9.86602 7.64634L7.51268 5.29301C7.31935 5.09967 6.99935 5.09967 6.80602 5.29301C6.61268 5.48634 6.61268 5.80634 6.80602 5.99967L8.80602 7.99967L6.80602 9.99967Z"
                    fill="#F9F9F9"
                  />
                </svg>
              </button>
            </div>

            <div v-if="!loading">
              <div v-if="learningGoals.length === 0" class="text-center py-8">
                <p class="text-muted-foreground text-[13px]">
                  No skill paths found. Create your first skill path to get
                  started!
                </p>
              </div>

              <div v-else class="relative">
                <div
                  ref="currentGoalsScroller"
                  class="current-goals-scroller flex gap-4 overflow-x-auto pb-2 -mx-1 px-1 pr-10 snap-x snap-mandatory"
                  style="-webkit-overflow-scrolling: touch"
                  @scroll.passive="onCurrentGoalsScroll"
                  @mouseenter="triggerSlideHint"
                  @focusin="triggerSlideHint"
                >
                  <div
                    v-for="(goal, idx) in learningGoals"
                    :key="goal.project_id"
                    class="snap-start flex-none w-[78%] sm:w-[62%] md:w-[calc(50%-0.5rem)]"
                  >
                    <div
                      class="relative overflow-hidden rounded-[12px] border border-border bg-card p-5 shadow-sm h-full"
                      :class="[
                        isProcessing(goal.status)
                          ? 'opacity-70 cursor-not-allowed'
                          : 'cursor-pointer hover:shadow-md',
                        isFailed(goal.status)
                          ? 'border-red-300 dark:border-red-900/50'
                          : 'border-border',
                      ]"
                      @click="handleGoalClick(goal)"
                    >
                      <div
                        class="absolute left-0 top-0 h-full w-[10px]"
                        :style="{ backgroundColor: goalAccentColor(goal, idx) }"
                      ></div>

                      <div class="pl-3">
                        <p
                          class="text-[14px] font-semibold text-foreground truncate"
                        >
                          {{ goal.topic }}
                        </p>
                        <p
                          class="text-[11px] text-muted-foreground mt-1 truncate"
                        >
                          {{
                            goal.description ||
                            "Usually helps you to become a wizard in frontend"
                          }}
                        </p>

                        <div class="mt-4">
                          <div
                            class="h-1.5 bg-muted rounded-full overflow-hidden"
                          >
                            <div
                              class="h-full bg-foreground rounded-full transition-all"
                              :style="{
                                width: `${goalProgressPercent(goal)}%`,
                              }"
                            ></div>
                          </div>

                          <div
                            class="mt-3 flex items-center justify-between text-[11px] text-muted-foreground"
                          >
                            <span
                              >{{ goalProgressPercent(goal) }}% complete</span
                            >
                            <span class="inline-flex items-center gap-1">
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 14 14"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M12.8346 7.00033C12.8346 10.2203 10.2213 12.8337 7.0013 12.8337C3.7813 12.8337 1.16797 10.2203 1.16797 7.00033C1.16797 3.78033 3.7813 1.16699 7.0013 1.16699C10.2213 1.16699 12.8346 3.78033 12.8346 7.00033Z"
                                  stroke="#ABABB3"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                                <path
                                  d="M9.16516 8.85503L7.35682 7.77586C7.04182 7.58919 6.78516 7.14003 6.78516 6.77253V4.38086"
                                  stroke="#ABABB3"
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                />
                              </svg>
                              {{ goalLessonsLeft(goal) }} lessons left
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div
                  v-if="learningGoals.length > 1"
                  class="pointer-events-none absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-card via-card/70 to-transparent transition-opacity duration-500"
                  :class="showSlideHint ? 'opacity-100' : 'opacity-0'"
                ></div>

                <div
                  v-if="learningGoals.length > 1"
                  class="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 flex items-center gap-1 px-2 py-1 rounded-full bg-black/70 text-white text-[10px] font-medium transition-opacity duration-500"
                  :class="showSlideHint ? 'opacity-100' : 'opacity-0'"
                >
                  <span>Swipe</span>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M4.5 9L7.5 6L4.5 3"
                      stroke="#FFFFFF"
                      stroke-width="1.5"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
              </div>
            </div>

            <div v-else class="text-center py-8">
              <Loader />
            </div>
          </div>

          <!-- Top Picks Section -->
          <div class="border border-border rounded-[12px] p-6 bg-card">
            <h2 class="text-[15px] font-semibold text-foreground mb-4">
              Top picks for {{ userName }}
            </h2>

            <div v-if="loadingSuggestedTopics" class="text-center py-8">
              <Loader />
            </div>

            <div
              v-else-if="topPicksGoals.length === 0"
              class="text-center py-8"
            >
              <p class="text-muted-foreground text-[13px]">
                No suggested topics available right now.
              </p>
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="topic in topPicksGoals"
                :key="topic.title"
                class="flex items-center justify-between p-4 bg-muted/30 rounded-[8px] border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                @click="navigateToCreate"
              >
                <div class="flex items-center gap-3 flex-1">
                  <div
                    class="w-8 h-8 flex-shrink-0 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center text-white text-[12px] font-semibold"
                  >
                    {{ topic.topic_name.charAt(0).toUpperCase() }}
                  </div>

                  <div class="flex-1 min-w-0">
                    <h3 class="text-[13px] font-medium text-foreground">
                      {{ topic.topic_name }}
                    </h3>
                    <p class="text-[11px] text-muted-foreground truncate">
                      {{ topic.description }}
                    </p>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <span
                    v-if="topic.skill_level"
                    class="px-3 py-1 text-[11px] font-medium rounded-md whitespace-nowrap bg-muted text-foreground"
                  >
                    {{ topic.skill_level }}
                  </span>

                  <button class="text-gray-400 hover:text-gray-600">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <circle cx="8" cy="4" r="1.5" />
                      <circle cx="8" cy="8" r="1.5" />
                      <circle cx="8" cy="12" r="1.5" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Popular Learning Paths -->
          <div class="border border-border rounded-[12px] p-6 bg-card">
            <h2 class="text-[15px] font-semibold text-foreground mb-4">
              Popular Learning Paths
            </h2>

            <div v-if="loadingPopularTopics" class="text-center py-8">
              <Loader />
            </div>

            <div v-else-if="popularGoals.length === 0" class="text-center py-8">
              <p class="text-muted-foreground text-[13px]">
                No popular topics available right now.
              </p>
            </div>

            <div v-else class="space-y-3">
              <div
                v-for="topic in popularGoals"
                :key="topic.title"
                class="flex items-center justify-between p-4 bg-muted/30 rounded-[8px] border border-border hover:bg-muted/50 cursor-pointer transition-colors"
                @click="navigateToCreate"
              >
                <div class="flex items-center gap-3 flex-1">
                  <div
                    class="w-8 h-8 flex-shrink-0 bg-gradient-to-br from-green-400 to-green-600 rounded-lg flex items-center justify-center text-white text-[12px] font-semibold"
                  >
                    {{ topic.topic_name.charAt(0).toUpperCase() }}
                  </div>

                  <div class="flex-1 min-w-0">
                    <h3 class="text-[13px] font-medium text-foreground">
                      {{ topic.topic_name }}
                    </h3>
                    <p class="text-[11px] text-muted-foreground truncate">
                      {{ topic.description }}
                    </p>
                  </div>
                </div>

                <div class="flex items-center gap-3">
                  <span
                    v-if="topic.skill_level"
                    class="px-3 py-1 text-[11px] font-medium rounded-md whitespace-nowrap bg-muted text-foreground"
                  >
                    {{ topic.skill_level }}
                  </span>

                  <button class="text-gray-400 hover:text-gray-600">
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                    >
                      <circle cx="8" cy="4" r="1.5" />
                      <circle cx="8" cy="8" r="1.5" />
                      <circle cx="8" cy="12" r="1.5" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Right Column - Charts -->
        <div class="space-y-6">
          <!-- Progress Donut Chart -->
          <div class="border border-border rounded-[12px] p-6 bg-card">
            <div class="mb-4">
              <h2 class="text-[15px] font-semibold text-foreground">
                Progress
              </h2>
              <p class="text-[11px] text-muted-foreground">
                By course completion
              </p>
            </div>

            <div
              class="flex justify-center my-6 relative mx-auto w-[228px] h-[228px]"
            >
              <canvas ref="progressChart"></canvas>
              <div
                class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none"
              >
                <span class="text-[32px] font-bold text-foreground">{{
                  learningGoals.length
                }}</span>
                <span class="text-[11px] text-muted-foreground"
                  >Learning Goals</span
                >
              </div>
            </div>

            <div class="flex items-center justify-center gap-4 text-[11px]">
              <div class="flex items-center gap-1.5">
                <div class="w-2.5 h-2.5 rounded-full bg-[#4CAF50]"></div>
                <span class="text-muted-foreground">Completed</span>
              </div>
              <div class="flex items-center gap-1.5">
                <div class="w-2.5 h-2.5 rounded-full bg-[#C8C8D2]"></div>
                <span class="text-muted-foreground">Not Started</span>
              </div>
              <div class="flex items-center gap-1.5">
                <div class="w-2.5 h-2.5 rounded-full bg-[#FFA726]"></div>
                <span class="text-muted-foreground">Active</span>
              </div>
            </div>
          </div>

          <!-- Productivity Bar Chart -->
          <div class="border border-border rounded-[12px] p-6 bg-card">
            <div class="mb-4">
              <h2 class="text-[15px] font-semibold text-foreground">
                Productivity Bar
              </h2>
              <p class="text-[11px] text-muted-foreground">Monthly Output</p>
            </div>

            <div class="h-[250px]">
              <canvas ref="productivityChart"></canvas>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Project Modal -->
    <DeleteProjectModal
      :isOpen="deleteModalOpen"
      :projectId="selectedProject.id"
      :projectName="selectedProject.name"
      @close="closeDeleteModal"
      @confirm-delete="confirmDeleteProject"
    />
  </div>
</template>

<script>
import apiClient from "@/axios";
import DeleteProjectModal from "../modals/DeleteProjectModal.vue";
import {
  Chart,
  ArcElement,
  DoughnutController,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import Loader from "../ui/Loader.vue";

Chart.register(
  ArcElement,
  DoughnutController,
  BarElement,
  BarController,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
);

export default {
  name: "LearningGoalComponent",
  components: {
    DeleteProjectModal,
    Loader,
  },
  data() {
    return {
      showBanner: false,
      appVersion: null,
      latestAppVersion: null,
      learningGoals: [],
      suggestedTopics: [],
      popularTopics: [],
      goalsSummary: null,
      monthlyProductivity: [],
      projectId: this.$route.params.projectId,
      loading: true,
      loadingSuggestedTopics: false,
      loadingPopularTopics: false,
      loadingProductivity: false,
      deleteModalOpen: false,
      selectedProject: {
        id: null,
        name: "",
      },
      showSlideHint: false,
      slideHintTimeoutId: null,
      progressChart: null,
      productivityChart: null,
      isUnmounting: false,
    };
  },
  computed: {
    isTalent() {
      return this.$store.getters.isTalent;
    },
    currentUser() {
      return this.$store.state.user || {};
    },
    userName() {
      return this.currentUser?.first_name || "User";
    },

    currentLearningGoals() {
      return this.learningGoals;
    },

    topPicksGoals() {
      return this.suggestedTopics.slice(0, 2);
    },
    popularGoals() {
      return this.popularTopics.slice(0, 2);
    },
    completedCount() {
      return this.goalsSummary?.total_completed || 0;
    },
    notStartedCount() {
      return this.goalsSummary?.total_not_started || 0;
    },
    activeCount() {
      return this.goalsSummary?.total_active || 0;
    },
  },
  watch: {},
  methods: {
    onSeeMoreClick() {
      this.$router.push({ name: "LearningGoalsAll" });
    },
    triggerSlideHint() {
      const scroller = this.$refs.currentGoalsScroller;
      if (!scroller) return;
      if (this.learningGoals.length <= 1) return;
      if (scroller.scrollWidth <= scroller.clientWidth) return;

      this.showSlideHint = true;
      if (this.slideHintTimeoutId) {
        clearTimeout(this.slideHintTimeoutId);
      }
      this.slideHintTimeoutId = setTimeout(() => {
        this.showSlideHint = false;
        this.slideHintTimeoutId = null;
      }, 2200);
    },
    onCurrentGoalsScroll() {
      this.triggerSlideHint();
    },
    async checkAppUpdate() {
      try {
        this.appVersion = localStorage.getItem("latestAppVersion") || null;

        const userAgent = navigator.userAgent.toLowerCase();
        let platform = "windows";

        if (/macintosh|mac os x/.test(userAgent)) {
          platform = "macos";
        }

        const response = await apiClient.get(
          "/general/desktop-app/check-update",
          {
            params: {
              platform: platform,
              client_version: this.appVersion,
            },
          },
        );

        if (response.data && response.data.has_update) {
          this.latestAppVersion = response.data.latest_version;
          this.showBanner = true;

          if (this.latestAppVersion) {
            localStorage.setItem("latestAppVersion", this.latestAppVersion);
          }
        } else {
          this.showBanner = false;
        }
      } catch (error) {
        console.error("Error checking app update:", error);
        this.showBanner = false;
      }
    },
    async downloadApp() {
      try {
        const userAgent = navigator.userAgent.toLowerCase();
        let platform = "windows";

        if (/macintosh|mac os x/.test(userAgent)) {
          platform = "macos";
        }

        const baseUrl = process.env.VUE_APP_API_BASE_URL;
        const downloadUrl = `${baseUrl}/general/desktop-app/download?platform=${platform}`;

        window.open(downloadUrl, "_blank");
      } catch (error) {
        console.error("Error downloading app:", error);
        this.$toast.error(
          "Download Failed",
          "Failed to download the app. Please try again.",
        );
      }
    },
    async fetchLearningGoals() {
      try {
        this.loading = true;
        const response = await apiClient.get(
          "/web/learning-goals/my-learning-goals",
        );
        this.learningGoals = response.data || [];

        this.$nextTick(() => {
          this.triggerSlideHint();
        });
      } catch (error) {
        console.error("Error fetching learning goals:", error);
        this.$toast.error(
          "Loading Failed",
          "Failed to load learning goals. Please try again.",
        );
      } finally {
        this.loading = false;
      }
    },

    async fetchSuggestedTopics() {
      try {
        this.loadingSuggestedTopics = true;
        // const response = await apiClient.get(
        //   "/web/learning-goals/suggested-topics"
        // );
        // this.suggestedTopics = response.data.topics || [];
        this.suggestedTopics = [];
      } catch (error) {
        console.error("Error fetching suggested topics:", error);
        this.suggestedTopics = [];
      } finally {
        this.loadingSuggestedTopics = false;
      }
    },

    async fetchPopularTopics() {
      try {
        this.loadingPopularTopics = true;
        // const response = await apiClient.get(
        //   "/web/learning-goals/popular-topics"
        // );
        // this.popularTopics = response.data.topics || [];
        this.popularTopics = [];
      } catch (error) {
        console.error("Error fetching popular topics:", error);
        this.popularTopics = [];
      } finally {
        this.loadingPopularTopics = false;
      }
    },

    async fetchGoalsSummary() {
      try {
        const response = await apiClient.get("/web/learning-goals/summary");
        this.goalsSummary = response.data || null;
        this.$nextTick(() => {
          this.initProgressChart();
        });
      } catch (error) {
        console.error("Error fetching goals summary:", error);
        this.goalsSummary = null;
      }
    },

    async fetchMonthlyProductivity() {
      try {
        this.loadingProductivity = true;
        const response = await apiClient.get(
          "/web/dashboard/talent/monthly-productivity",
          { params: { months: 5 } },
        );
        this.monthlyProductivity = response.data.monthly_data || [];
        this.$nextTick(() => {
          this.initProductivityChart();
        });
      } catch (error) {
        console.error("Error fetching monthly productivity:", error);
        this.monthlyProductivity = [];
      } finally {
        this.loadingProductivity = false;
      }
    },
    initProgressChart() {
      if (this.isUnmounting) return;
      const canvas = this.$refs.progressChart;
      if (!canvas) return;

      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      if (this.progressChart) {
        this.progressChart.destroy();
        this.progressChart = null;
      }

      this.progressChart = new Chart(ctx, {
        type: "doughnut",
        data: {
          labels: ["Completed", "Not Started", "Active"],
          datasets: [
            {
              data: [
                this.completedCount,
                this.notStartedCount,
                this.activeCount,
              ],
              backgroundColor: ["#4CAF50", "#C8C8D2", "#FFA726"],
              borderWidth: 0,
              borderRadius: 8,
              spacing: 2,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "75%",
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              backgroundColor: "#1A1A1A",
              titleColor: "#FFFFFF",
              bodyColor: "#FFFFFF",
              padding: 12,
              cornerRadius: 8,
              displayColors: true,
              callbacks: {
                label: function (context) {
                  const label = context.label || "";
                  const value = context.parsed || 0;
                  return `${label}: ${value} goal${value !== 1 ? "s" : ""}`;
                },
              },
            },
          },
        },
      });
    },
    initProductivityChart() {
      if (this.isUnmounting) return;
      const canvas = this.$refs.productivityChart;
      if (!canvas) {
        console.log("Canvas element not found for productivity chart");
        return;
      }

      const ctx = canvas.getContext("2d");
      if (!ctx) {
        console.log("Canvas context not found for productivity chart");
        return;
      }

      if (this.productivityChart) {
        this.productivityChart.destroy();
        this.productivityChart = null;
      }

      if (!this.monthlyProductivity || this.monthlyProductivity.length === 0) {
        console.log("No productivity data available");
        return;
      }

      const months = this.monthlyProductivity.map((m) =>
        m.month.substring(0, 3).toUpperCase(),
      );
      const data = this.monthlyProductivity.map((m) => m.count);

      this.productivityChart = new Chart(ctx, {
        type: "bar",
        data: {
          labels: months,
          datasets: [
            {
              data: data,
              backgroundColor: [
                "#3D3D3D",
                "#3D3D3D",
                "#3D3D3D",
                "#3D3D3D",
                "#3D3D3D",
              ],
              borderRadius: 6,
              barThickness: 40,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: {
              enabled: true,
              backgroundColor: "#1A1A1A",
              padding: 12,
              cornerRadius: 8,
              displayColors: false,
              callbacks: {
                label: function (context) {
                  return `Output: ${context.parsed.y}`;
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              border: { display: true },
              ticks: {
                color: "#667085",
                font: { size: 11 },
              },
            },
            y: {
              beginAtZero: true,
              max: 100,
              grid: {
                color: "#F2F4F7",
                drawBorder: false,
              },
              border: { display: false },
              ticks: {
                color: "#667085",
                font: { size: 11 },
                stepSize: 20,
              },
            },
          },
        },
      });
    },
    goalProgressPercent(goal) {
      const raw = goal?.progress;
      const value = typeof raw === "number" ? raw : Number(raw);
      if (!Number.isFinite(value)) return 0;
      return Math.max(0, Math.min(100, Math.round(value)));
    },
    goalLessonsLeft(goal) {
      return goal?.lessons_left ?? goal?.lessons_count ?? 5;
    },
    goalAccentColor(goal, idx) {
      const status = goal?.status?.toLowerCase?.() || "";
      if (status === "completed") return "#D8F5E0";
      if (status === "failed") return "#FEE2E2";
      if (status === "processing") return "#E0E7FF";
      if (status === "active" || status === "in_progress") return "#FBE7D2";
      // fallback to alternating accent like the mock
      return idx % 2 === 0 ? "#D8F5E0" : "#FBE7D2";
    },
    isProcessing(status) {
      return (status || "").toString().toLowerCase() === "processing";
    },
    isFailed(status) {
      return (status || "").toString().toLowerCase() === "failed";
    },
    getStatusColor(status) {
      switch (status?.toLowerCase()) {
        case "completed":
          return {
            text: "#14C100",
            bg: "#F0FDF4",
            border: "#32F72F",
            progressBg: "#292D32",
          };
        case "processing":
          return {
            text: "#6366f1",
            bg: "#e0e7ff",
            border: "#818cf8",
            progressBg: "#6366f1",
          };
        case "failed":
          return {
            text: "#DC2626",
            bg: "#FEE2E2",
            border: "#EF4444",
            progressBg: "#EF4444",
          };
        case "in_progress":
        case "active":
          return {
            text: "#F59E0B",
            bg: "#FEF3C7",
            border: "#F59E0B",
            progressBg: "#F59E0B",
          };
        default:
          return {
            text: "#EF4444",
            bg: "#FEF2F2",
            border: "#EF4444",
            progressBg: "#EF4444",
          };
      }
    },
    getStatusText(status) {
      switch (status?.toLowerCase()) {
        case "completed":
          return "Completed";
        case "processing":
          return "Processing";
        case "failed":
          return "Failed";
        case "in_progress":
        case "active":
          return "In Progress";
        default:
          return "Not Started";
      }
    },
    handleGoalClick(goal) {
      if (this.isProcessing(goal?.status)) {
        this.$toast?.info?.(
          "Processing",
          "This learning goal is still being generated. Please wait a moment.",
        );
        return;
      }
      this.navigateToDetails(goal.project_id);
    },
    navigateToDetails(projectId) {
      this.$router.push({
        name: "LearningGoalDetails",
        params: { id: projectId },
      });
    },
    navigateToCreate() {
      this.$router.push({
        name: "CreateLearningGoal",
      });
    },
    openDeleteModal(goal) {
      this.selectedProject = {
        id: goal.project_id,
        name: goal.topic,
      };
      this.deleteModalOpen = true;
    },
    closeDeleteModal() {
      this.deleteModalOpen = false;
      this.selectedProject = {
        id: null,
        name: "",
      };
    },
    async confirmDeleteProject() {
      if (this.selectedProject.id) {
        await this.deleteLearningGoal(this.selectedProject.id);
      }
    },
    async deleteLearningGoal(id) {
      try {
        await apiClient.delete(`/web/learning-goals/${id}`);
        this.$toast.success("Success", "Learning goal deleted successfully");
        this.fetchLearningGoals();
        this.closeDeleteModal();
      } catch (error) {
        console.error("Error deleting learning goal:", error);
        this.$toast.error(
          "Delete Failed",
          "Failed to delete learning goal. Please try again.",
        );
      }
    },
  },
  created() {
    this.checkAppUpdate();
    this.fetchLearningGoals();
    this.fetchSuggestedTopics();
    this.fetchPopularTopics();
    this.fetchGoalsSummary();
    this.fetchMonthlyProductivity();
  },
  beforeUnmount() {
    this.isUnmounting = true;
    if (this.slideHintTimeoutId) {
      clearTimeout(this.slideHintTimeoutId);
      this.slideHintTimeoutId = null;
    }
    if (this.progressChart) {
      this.progressChart.destroy();
      this.progressChart = null;
    }
    if (this.productivityChart) {
      this.productivityChart.destroy();
      this.productivityChart = null;
    }
  },
};
</script>

<style scoped>
.current-goals-scroller {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}
.current-goals-scroller::-webkit-scrollbar {
  display: none; /* Chrome/Safari */
}
</style>
